import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Straight from '../../../assets/images/pluvaAutoLanding/rtk_straight.gif';
import Reverse from '../../../assets/images/pluvaAutoLanding/rtk_reverse.gif';
import UTurn from '../../../assets/images/pluvaAutoLanding/rtk_u-turn.gif';
import AutoTurn from '../../../assets/images/pluvaAutoLanding/rtk_auto-turn.gif';
import DriveModeObject from '../../../assets/images/pluvaAutoLanding/drive-mode_object.jpg';
import { PointBadge } from './PointBadge';

interface TechnicalDescriptionProps {
  title: string;
  gifImage: string;
  badgeName: string;
}

const technicalDescriptions: TechnicalDescriptionProps[] = [
  {
    title: 'A/B 직진 기능',
    gifImage: Straight,
    badgeName: '22년 07월 업데이트',
  },
  {
    title: 'A/B 후진 기능',
    gifImage: Reverse,
    badgeName: '24년 02월 업데이트',
  },
  {
    title: '트랙터를 사용한 자동 유턴과 골띄기',
    gifImage: UTurn,
    badgeName: '24년 03월 업데이트',
  },
  {
    title: '이앙기를 사용한 직진과 자동 선회',
    gifImage: AutoTurn,
    badgeName: '24년 04월 업데이트',
  },
];

const useLazyLoading = (targetRef: React.RefObject<HTMLImageElement>) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    const observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          setIsVisible(true);
        }
      });
    });

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, [targetRef]);

  return isVisible;
};

const TechnicalDescription = ({ detail }: { detail: TechnicalDescriptionProps }) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const isVisible = useLazyLoading(imgRef);
  const { title, gifImage, badgeName } = detail;

  return (
    <TechnicalDescriptionCard>
      <TechnicalContentWrapper>
        <TechnicalContent>
          <TechnicalTitle>{title}</TechnicalTitle>
          <TechnicalBadge>
            <TechnicalBadgeText>{badgeName}</TechnicalBadgeText>
          </TechnicalBadge>
        </TechnicalContent>
      </TechnicalContentWrapper>
      <TechnicalDescriptionImage src={isVisible ? gifImage : undefined} ref={imgRef} />
    </TechnicalDescriptionCard>
  );
};

export const DriveModeSection = () => {
  return (
    <Container>
      <TitleSection>
        <PointBadge title={'주행 모드'} />
        <Title>{'한번 사면 계속 업데이트!\n플루바오토의 기능!'}</Title>
        <Image src={DriveModeObject} />
        <TitleHelperText>{'한국 기술진을 보유한 플루바오토는\n업그레이드되는 핸드폰/PC 방식 차용!'}</TitleHelperText>
        <TitleHelperTextHighlight>
          {'한국 농업 맞춤 새 기능과\n기존 기능 업그레이드를\n끝없이 제공해 드립니다'}
        </TitleHelperTextHighlight>
      </TitleSection>

      <ContentSection>
        <TechnicalDescriptionContainer>
          <TechnicalDescriptionList>
            {technicalDescriptions.map((technicalDetail) => {
              return <TechnicalDescription key={technicalDetail.title} detail={technicalDetail} />;
            })}
          </TechnicalDescriptionList>
        </TechnicalDescriptionContainer>
      </ContentSection>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  padding: 48px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  align-self: stretch;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;

  white-space: pre-line;
`;

const Title = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XXX-Large/xxxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */
`;

const Image = styled.img`
  width: 180px;
`;

const TitleHelperText = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  white-space: pre-line;
`;

const TitleHelperTextHighlight = styled.p`
  color: var(--Light-Scheme-Color-primary-green, #34c77e);
  text-align: center;

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const TechnicalDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const TechnicalDescriptionList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const TechnicalDescriptionCard = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
`;

const TechnicalDescriptionImage = styled.img`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-black, #232429);
`;

const TechnicalContentWrapper = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const TechnicalContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const TechnicalTitle = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const TechnicalBadge = styled.div`
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 2px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-green, #34c77e);
`;

const TechnicalBadgeText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);
  text-align: center;

  /* System/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 11.9px */
`;

const TechnicalContentText = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;
